import { PurchasedEnergyType } from '../generated/graphql'
import { lerp } from '../util/lerp'
import { energyTypeToEmissionCoefficientMap } from './emission-coefficients'

function getYearlyProgress(
  startYear: number,
  endYear: number,
  currentYear: number,
): number {
  const fullDistance = endYear - startYear
  const currentDistance = currentYear - startYear
  return currentDistance / fullDistance
}

/**
 * Get the linearly interpolated emission coefficient for different energy types
 *
 * @param energyType Energy type of the emission
 * @param year Year of the emission
 * @returns coefficient with the unit kg CO₂e / MWh
 */
export function getEmissionCoefficient(
  energyType: PurchasedEnergyType,
  year: number,
): number {
  const coefficientsByYear = energyTypeToEmissionCoefficientMap[energyType]
  if (coefficientsByYear[year] !== undefined) {
    return coefficientsByYear[year]
  }

  const years = Object.keys(coefficientsByYear).map((coefficientYear) =>
    Number(coefficientYear),
  )

  const largestYear = years[years.length - 1]
  if (year > largestYear) {
    return coefficientsByYear[largestYear]
  }

  const smallestYear = years[0]
  if (year < smallestYear) {
    return coefficientsByYear[smallestYear]
  }

  const smallerYear = years.find(
    (_, i) => years[i] < year && year < years[i + 1],
  )
  const largerYear = years.find(
    (_, i) => years[i - 1] < year && year < years[i],
  )
  if (!smallerYear || !largerYear) {
    throw new Error(
      'Error getting year from emission coeffients. Please check that all the coefficients are in order by year.',
    )
  }

  const progress = getYearlyProgress(smallerYear, largerYear, year)
  return lerp(
    coefficientsByYear[smallerYear],
    coefficientsByYear[largerYear],
    progress,
  )
}
